import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { BulkImportResult, Tag, User } from "@/types";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";

export default function Students() {
  const [contacts, setContacts] = useState<User[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);

  // Single add form
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [singleTagIds, setSingleTagIds] = useState<Set<string>>(new Set());
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Bulk form
  const [bulkText, setBulkText] = useState("");
  const [bulkTagIds, setBulkTagIds] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BulkImportResult | null>(null);
  const [bulking, setBulking] = useState(false);

  // Filter by tag
  const [filterTagId, setFilterTagId] = useState<string | null>(null);

  // Inline tag editor
  const [editingTagsFor, setEditingTagsFor] = useState<string | null>(null);

  // Inline contact editor (nome/cognome/telefono)
  const [editingContactFor, setEditingContactFor] = useState<string | null>(null);

  function load() {
    setLoading(true);
    Promise.all([
      api.get<User[]>("/admin/students"),
      api.get<Tag[]>("/admin/tags"),
    ])
      .then(([c, t]) => { setContacts(c); setTags(t); })
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  // Separazione pending vs approvati. I pending hanno la loro sezione in cima.
  const pending = useMemo(
    () => contacts.filter((c: any) => c.pendingApproval === true),
    [contacts],
  );
  const approved = useMemo(
    () => contacts.filter((c: any) => c.pendingApproval !== true),
    [contacts],
  );

  const filtered = useMemo(() => {
    if (!filterTagId) return approved;
    return approved.filter((c) => c.tags?.some((t) => t.id === filterTagId));
  }, [approved, filterTagId]);

  async function approve(id: string) {
    await api.post(`/admin/students/${id}/approve`);
    load();
  }
  async function reject(id: string, email: string) {
    if (!confirm(`Rifiutare la registrazione di "${email}"? Il record verrà eliminato.`)) return;
    await api.post(`/admin/students/${id}/reject`);
    load();
  }

  async function addOne(ev: React.FormEvent) {
    ev.preventDefault();
    setAddError(null); setAdding(true);
    try {
      await api.post("/admin/students", {
        email: email.trim().toLowerCase(),
        nome: nome.trim() || undefined,
        cognome: cognome.trim() || undefined,
        telefono: telefono.trim() || undefined,
        tagIds: Array.from(singleTagIds),
      });
      setEmail(""); setNome(""); setCognome(""); setTelefono("");
      setSingleTagIds(new Set());
      load();
    } catch (e: any) {
      setAddError(e.message);
    } finally {
      setAdding(false);
    }
  }

  async function addBulk(ev: React.FormEvent) {
    ev.preventDefault();
    setBulkResult(null); setBulking(true);
    try {
      const emails = bulkText.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
      if (emails.length === 0) {
        setBulkResult({ importate: 0, duplicate: [], invalide: [], errori: ["Nessuna email"] });
        return;
      }
      const r = await api.post<BulkImportResult>("/admin/students/bulk", {
        emails,
        tagIds: Array.from(bulkTagIds),
      });
      setBulkResult(r);
      setBulkText("");
      load();
    } catch (e: any) {
      setBulkResult({ importate: 0, duplicate: [], invalide: [], errori: [e.message] });
    } finally {
      setBulking(false);
    }
  }

  async function toggleActive(id: string) {
    await api.post(`/admin/students/${id}/toggle-active`);
    load();
  }

  async function saveTagsFor(userId: string, tagIds: string[]) {
    await api.put(`/admin/students/${userId}/tags`, { tagIds });
    setEditingTagsFor(null);
    load();
  }

  async function saveContactFor(
    userId: string,
    data: { email: string; nome: string; cognome: string; telefono: string },
    originalEmail: string,
  ) {
    try {
      const payload: Record<string, string | null> = {
        nome: data.nome.trim() || null,
        cognome: data.cognome.trim() || null,
        telefono: data.telefono.trim() || null,
      };
      // Mando email solo se è cambiata (evita rewrite inutili).
      const newEmail = data.email.trim().toLowerCase();
      if (newEmail && newEmail !== originalEmail.toLowerCase()) {
        payload.email = newEmail;
      }
      await api.put(`/admin/students/${userId}`, payload);
      setEditingContactFor(null);
      load();
    } catch (e: any) {
      alert(e.message);
    }
  }

  function toggleSet(set: Set<string>, id: string, setter: (s: Set<string>) => void) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline">
        <h1 className="text-3xl font-serif text-primary">Contatti</h1>
        <div className="flex gap-3">
          <Link to="/admin/tag" className="font-mono text-[17px] text-secondary hover:text-primary">
            tag →
          </Link>
          <Link to="/admin/dashboard" className="font-mono text-[17px] text-secondary hover:text-primary">
            ← dashboard
          </Link>
        </div>
      </header>

      {pending.length > 0 && (
        <Card className="border-accent">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono text-xs uppercase tracking-wider text-accent">
              ⏳ In attesa di approvazione ({pending.length})
            </h2>
          </div>
          <div className="space-y-1">
            {pending.map((p) => {
              const fonte = (p as any).fonteRegistrazione as string | null;
              return (
                <div key={p.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-primary">
                      {`${p.nome ?? ""} ${p.cognome ?? ""}`.trim() || <span className="text-muted">—</span>}
                    </div>
                    <div className="text-xs text-muted font-mono truncate">
                      {p.email}
                      {p.telefono && <span> · {p.telefono}</span>}
                    </div>
                    {fonte && (
                      <div className="mt-1">
                        <Badge variant="outline" color="#5B3E8A">fonte: {fonte}</Badge>
                      </div>
                    )}
                  </div>
                  <button onClick={() => approve(p.id)}
                    className="bg-green text-white px-3 py-1.5 rounded-lg font-mono text-xs hover:brightness-110">
                    ✓ Approva
                  </button>
                  <button onClick={() => reject(p.id, p.email)}
                    className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-red-500 hover:text-red-300">
                    ✕ Rifiuta
                  </button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
            Aggiungi singolo
          </h2>
          <form onSubmit={addOne} className="space-y-3">
            <Input label="Email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} required />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
              <Input label="Cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} />
            </div>
            <Input label="Telefono (E.164: +393331234567)" value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="+39 333 123 4567" />
            {tags.length > 0 && (
              <TagPicker
                tags={tags}
                selected={singleTagIds}
                onToggle={(id) => toggleSet(singleTagIds, id, setSingleTagIds)}
              />
            )}
            {addError && <div className="text-red-300 text-sm">{addError}</div>}
            <Button type="submit" loading={adding}>Aggiungi</Button>
          </form>
        </Card>

        <Card>
          <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
            Import bulk (una email per riga)
          </h2>
          <form onSubmit={addBulk} className="space-y-3">
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={5}
              placeholder={"mario@esempio.it\nlucia@esempio.it\n..."}
              className="w-full bg-bg border border-border rounded-lg p-3 text-primary placeholder:text-muted focus:outline-none focus:border-accent font-mono text-sm"
            />
            {tags.length > 0 && (
              <TagPicker
                tags={tags}
                selected={bulkTagIds}
                onToggle={(id) => toggleSet(bulkTagIds, id, setBulkTagIds)}
                label="Applica questi tag a tutto il batch"
              />
            )}
            <Button type="submit" loading={bulking}>Importa</Button>
            {bulkResult && (
              <div className="text-sm space-y-1 font-mono">
                {bulkResult.importate > 0 && <div className="text-green">✓ Importate: {bulkResult.importate}</div>}
                {bulkResult.duplicate.length > 0 && <div className="text-secondary">⊗ Duplicate ({bulkResult.duplicate.length})</div>}
                {bulkResult.invalide.length > 0 && <div className="text-red-300">⚠ Invalide: {bulkResult.invalide.join(", ")}</div>}
                {bulkResult.errori.length > 0 && <div className="text-red-300">! {bulkResult.errori.join("; ")}</div>}
              </div>
            )}
          </form>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-mono text-xs uppercase tracking-wider text-secondary">
            Lista contatti ({filtered.length}{filterTagId && ` / ${contacts.length}`})
          </h2>
          {tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted font-mono">filtra:</span>
              <button
                onClick={() => setFilterTagId(null)}
                className={`text-xs font-mono px-2 py-1 rounded ${!filterTagId ? "bg-accent text-white" : "text-secondary hover:text-primary"}`}>
                tutti
              </button>
              {tags.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setFilterTagId(filterTagId === t.id ? null : t.id)}
                  style={{ backgroundColor: filterTagId === t.id ? t.colore : undefined }}
                  className={`text-xs font-mono px-2 py-1 rounded border ${filterTagId === t.id ? "text-white border-transparent" : "border-border text-secondary hover:text-primary"}`}>
                  {t.nome}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Spinner size={24} /></div>
        ) : filtered.length === 0 ? (
          <p className="text-secondary text-sm">Nessun contatto.</p>
        ) : (
          <div className="space-y-1">
            {filtered.map((s) => (
              <div key={s.id} className="py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-primary">
                      {(s.nome || s.cognome)
                        ? `${s.nome ?? ""} ${s.cognome ?? ""}`.trim()
                        : <span className="text-muted">—</span>}
                    </div>
                    <div className="text-xs text-muted font-mono truncate">
                      {s.email}
                      {s.telefono && <span> · {s.telefono}</span>}
                    </div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {(s.tags ?? []).map((t) => (
                        <Badge key={t.id} color={t.colore}>{t.nome}</Badge>
                      ))}
                    </div>
                  </div>
                  {s.active
                    ? <Badge color="#2A7A4B">attivo</Badge>
                    : <Badge color="#555577">disattivo</Badge>}
                  <button onClick={() => {
                      setEditingContactFor(editingContactFor === s.id ? null : s.id);
                      setEditingTagsFor(null);
                    }}
                    className="bg-card border border-border text-primary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-accent">
                    Modifica
                  </button>
                  <button onClick={() => {
                      setEditingTagsFor(editingTagsFor === s.id ? null : s.id);
                      setEditingContactFor(null);
                    }}
                    className="bg-card border border-border text-primary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-accent">
                    Tag
                  </button>
                  <button onClick={() => toggleActive(s.id)}
                    className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-secondary">
                    {s.active ? "Disattiva" : "Riattiva"}
                  </button>
                </div>

                {editingContactFor === s.id && (
                  <div className="mt-3 pl-4 border-l-2 border-accent space-y-2">
                    <InlineContactEditor
                      current={{
                        email: s.email,
                        nome: s.nome ?? "",
                        cognome: s.cognome ?? "",
                        telefono: s.telefono ?? "",
                      }}
                      onSave={(d) => saveContactFor(s.id, d, s.email)}
                      onCancel={() => setEditingContactFor(null)}
                    />
                  </div>
                )}

                {editingTagsFor === s.id && (
                  <div className="mt-3 pl-4 border-l-2 border-accent space-y-2">
                    <InlineTagEditor
                      allTags={tags}
                      current={(s.tags ?? []).map((t) => t.id)}
                      onSave={(ids) => saveTagsFor(s.id, ids)}
                      onCancel={() => setEditingTagsFor(null)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function TagPicker({
  tags, selected, onToggle, label,
}: {
  tags: Tag[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  label?: string;
}) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-wider text-secondary mb-1.5">
        {label ?? "Tag"}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => {
          const sel = selected.has(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onToggle(t.id)}
              style={sel ? { backgroundColor: t.colore, borderColor: t.colore } : { borderColor: t.colore, color: t.colore }}
              className={`text-xs font-mono px-2 py-1 rounded border ${sel ? "text-white" : ""}`}>
              {sel ? "✓ " : ""}{t.nome}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function InlineTagEditor({
  allTags, current, onSave, onCancel,
}: {
  allTags: Tag[];
  current: string[];
  onSave: (ids: string[]) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(current));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {allTags.map((t) => {
          const sel = selected.has(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                const next = new Set(selected);
                if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                setSelected(next);
              }}
              style={sel ? { backgroundColor: t.colore, borderColor: t.colore } : { borderColor: t.colore, color: t.colore }}
              className={`text-xs font-mono px-2 py-1 rounded border ${sel ? "text-white" : ""}`}>
              {sel ? "✓ " : ""}{t.nome}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(Array.from(selected))}
          className="bg-accent text-white px-3 py-1.5 rounded-lg font-mono text-xs hover:brightness-110">
          Salva
        </button>
        <button onClick={onCancel}
          className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs">
          Annulla
        </button>
      </div>
    </div>
  );
}

function InlineContactEditor({
  current, onSave, onCancel,
}: {
  current: { email: string; nome: string; cognome: string; telefono: string };
  onSave: (d: { email: string; nome: string; cognome: string; telefono: string }) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState(current.email);
  const [nome, setNome] = useState(current.nome);
  const [cognome, setCognome] = useState(current.cognome);
  const [telefono, setTelefono] = useState(current.telefono);
  const emailChanged = email.trim().toLowerCase() !== current.email.toLowerCase();

  return (
    <div className="space-y-3">
      <Input label="Email" type="email" value={email}
        onChange={(e) => setEmail(e.target.value)} required />
      {emailChanged && (
        <div className="text-xs text-accent font-mono">
          ⚠ Stai cambiando l'email. I magic link già emessi continuano a funzionare
          (sono legati all'ID interno), ma le nuove notifiche partiranno verso il
          nuovo indirizzo.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
        <Input label="Cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} />
      </div>
      <Input label="Telefono (E.164: +393331234567)" value={telefono}
        onChange={(e) => setTelefono(e.target.value)}
        placeholder="+39 333 123 4567" />
      <div className="flex gap-2">
        <button onClick={() => onSave({ email, nome, cognome, telefono })}
          className="bg-accent text-white px-3 py-1.5 rounded-lg font-mono text-xs hover:brightness-110">
          Salva
        </button>
        <button onClick={onCancel}
          className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs">
          Annulla
        </button>
      </div>
    </div>
  );
}
